import { useQuery } from '@tanstack/react-query';
import {
  type EmployeeListItem,
  employmentLabel,
  type Relationship,
  formatMoney,
  RATE_UNIT_LABELS,
  salaryPeriodLabel,
  TFR_DESTINATION_LABELS,
} from '@k-management/shared';
import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { DataGrid, type GridColumn } from '../../components/DataGrid.js';
import { Icon } from '../../components/Icon.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { EmployeeDialog } from './EmployeeDialog.js';
import { PeopleViews, peopleKeys, SalaryStatePill } from './people-ui.js';
import { useListFilters } from '../../lib/list-filters.js';

const today = () => new Date().toISOString().slice(0, 10);

/** Lower case without accents: «Città» is found by «citta». */
const normalise = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Organizzazione › Persone (07/10/2026): the employees and, apart, the
 * collaborators (2.40), each in its own box with its own columns and its own
 * «Nuovo…». A click opens the card; the views «Stipendi del mese» and «Costo
 * del personale» (2.37) are the other two faces of the same area.
 */
export function EmployeesPage() {
  usePageTitle('Persone');
  const navigate = useNavigate();
  const canWrite = useCan('persone.write');
  // The agreement of the collaborators comes from the API only with the feature (2.40).
  const canSeeCosts = useCan('persone.costs');
  const [creating, setCreating] = useState<Relationship | null>(null);
  // Back from «Nuovo soggetto» with the subject of a new collaborator (2.39).
  const [search, setSearch] = useSearchParams();
  const [fromSubject] = useState(() => search.get('cliente'));
  useEffect(() => {
    if (!fromSubject) return;
    setCreating('collaboratore');
    setSearch({}, { replace: true });
    // Only at the arrival from Anagrafica.
  }, []);
  // The filters stay while the browser is open: found again coming back to the list (1.61).
  const { filters, setFilter, reset, changed } = useListFilters('people', {
    showGone: false as boolean,
    query: '',
  });
  const { showGone, query } = filters;
  const employees = useQuery({
    queryKey: peopleKeys.employees,
    queryFn: () => apiRequest<EmployeeListItem[]>('/people/employees'),
  });
  const gone = (item: EmployeeListItem) => item.leftOn !== null && item.leftOn < today();
  const all = employees.data ?? [];
  const needle = normalise(query);
  // One search and one «Anche i cessati» for both boxes.
  const shown = all.filter(
    (item) =>
      (showGone || !gone(item)) &&
      (!needle ||
        normalise(
          [item.lastName, item.firstName, item.taxCode ?? '', item.subjectName ?? ''].join(' '),
        ).includes(needle)),
  );
  const staff = shown.filter((item) => item.relationship === 'dipendente');
  const collaborators = shown.filter((item) => item.relationship === 'collaboratore');

  const nameColumn: GridColumn<EmployeeListItem> = {
    key: 'name',
    label: 'Persona',
    required: true,
    value: (item) => `${item.lastName} ${item.firstName}`,
    render: (item) => (
      <strong>
        {item.lastName} {item.firstName}
      </strong>
    ),
  };
  const roleColumn: GridColumn<EmployeeListItem> = {
    key: 'role',
    label: 'Ruolo aziendale',
    value: (item) => item.roleName,
    render: (item) => item.roleName ?? '—',
  };
  const fromColumn: GridColumn<EmployeeListItem> = {
    key: 'hired',
    label: 'Dal',
    value: (item) => item.hiredOn,
    render: (item) => formatDate(item.hiredOn),
  };
  const stateColumn: GridColumn<EmployeeListItem> = {
    key: 'state',
    label: 'Stato',
    value: (item) => (gone(item) ? 1 : 0),
    render: (item) =>
      gone(item) ? (
        <span className="pill muted">Cessato il {formatDate(item.leftOn!)}</span>
      ) : (
        <span className="pill ok">In forza</span>
      ),
  };
  const staffColumns: GridColumn<EmployeeListItem>[] = [
    nameColumn,
    {
      key: 'contract',
      label: 'Contratto',
      value: (item) => employmentLabel(item),
      render: (item) => employmentLabel(item),
    },
    roleColumn,
    fromColumn,
    {
      key: 'tfr',
      label: 'TFR',
      hiddenByDefault: true,
      value: (item) => item.pensionFundName ?? (item.tfr ? TFR_DESTINATION_LABELS[item.tfr] : null),
      render: (item) => item.pensionFundName ?? (item.tfr ? TFR_DESTINATION_LABELS[item.tfr] : '—'),
    },
    {
      key: 'tfrCovered',
      label: 'TFR coperto fino al',
      value: (item) => item.tfrCoveredTo,
      render: (item) =>
        item.tfrCoveredTo === null && item.tfrUncovered === 0 ? (
          '—'
        ) : (
          <span>
            {item.tfrCoveredTo ? formatDate(item.tfrCoveredTo) : 'nessun periodo'}
            {item.tfrUncovered > 0 && (
              <>
                {' '}
                <span className="pill warn">{item.tfrUncovered} da indicare</span>
              </>
            )}
          </span>
        ),
    },
    {
      key: 'salary',
      label: 'Ultimo stipendio',
      value: (item) =>
        item.lastSalary ? item.lastSalary.year * 100 + item.lastSalary.month : null,
      render: (item) =>
        item.lastSalary ? (
          <span>
            {salaryPeriodLabel(item.lastSalary.year, item.lastSalary.month)}{' '}
            <SalaryStatePill state={item.lastSalary.state} />
            {item.lastSalary.residual !== null && Number(item.lastSalary.residual) > 0 && (
              <small className="muted"> resta {formatMoney(item.lastSalary.residual)}</small>
            )}
          </span>
        ) : (
          '—'
        ),
    },
    stateColumn,
  ];
  const rateColumn: GridColumn<EmployeeListItem> = {
    key: 'rate',
    label: 'Accordo in vigore',
    value: (item) => (item.currentRate ? Number(item.currentRate.hourly) : null),
    render: (item) => {
      const rate = item.currentRate;
      if (!rate) return <span className="muted">da indicare</span>;
      return (
        <span>
          {formatMoney(rate.amount)} {RATE_UNIT_LABELS[rate.unit]}
          {rate.unit === 'giorno' && (
            <small className="muted"> ({formatMoney(rate.hourly)} all’ora)</small>
          )}
          <small className="muted">
            {rate.validTo && rate.validFrom <= today()
              ? ` fino al ${formatDate(rate.validTo)}`
              : ` dal ${formatDate(rate.validFrom)}`}
          </small>
        </span>
      );
    },
  };
  const collaboratorColumns: GridColumn<EmployeeListItem>[] = [
    nameColumn,
    {
      key: 'supplier',
      label: 'Fornitore in Anagrafica',
      value: (item) => item.subjectName,
      render: (item) =>
        !item.subjectName ? (
          '—'
        ) : item.subjectCompany ? (
          <span>
            <span className="muted">tramite</span> <strong>{item.subjectName}</strong>
          </span>
        ) : (
          item.subjectName
        ),
    },
    { ...roleColumn, hiddenByDefault: true },
    fromColumn,
    ...(canSeeCosts ? [rateColumn] : []),
    stateColumn,
  ];

  const box = (
    relationship: Relationship,
    title: string,
    rows: EmployeeListItem[],
    columns: GridColumn<EmployeeListItem>[],
    empty: ReactNode,
  ) => (
    <div className="card stack">
      <div className="row between">
        <h2>
          {title} <span className="count-pill">{rows.length}</span>
        </h2>
        {canWrite && (
          <button
            type="button"
            className={relationship === 'dipendente' ? 'primary small' : 'small'}
            onClick={() => setCreating(relationship)}
          >
            {relationship === 'dipendente' ? 'Nuovo dipendente' : 'Nuovo collaboratore'}
          </button>
        )}
      </div>
      <DataGrid
        id={relationship === 'dipendente' ? 'employees' : 'collaborators'}
        searchable={false}
        columns={columns}
        rows={rows}
        rowKey={(item) => item.id}
        onRowOpen={(item) => void navigate(`/organizzazione/persone/${item.id}`)}
        openOnClick
        empty={needle ? 'Nessuna persona trovata.' : empty}
      />
    </div>
  );

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Persone</h1>
          <p className="muted">
            I dipendenti di K-Rev, con i loro stipendi, e i collaboratori esterni che fatturano,
            anche tramite una società. Dati riservati: li vede solo chi ha il permesso «Persone».
          </p>
        </div>
      </div>
      <PeopleViews current="persone" />
      {employees.isError && <p className="error">{employees.error.message}</p>}
      {employees.data && (
        <>
          <div className="grid-toolbar">
            <label className="grid-search">
              <Icon name="search" size={16} />
              <input
                type="search"
                value={query}
                placeholder="Nome, codice fiscale o fornitore"
                aria-label="Cerca tra le persone"
                onChange={(event) => setFilter('query', event.target.value)}
              />
            </label>
            {all.some(gone) && (
              <label className="check small">
                <input
                  type="checkbox"
                  checked={showGone}
                  onChange={(event) => setFilter('showGone', event.target.checked)}
                />{' '}
                Anche i cessati
              </label>
            )}
            {changed && (
              <button type="button" className="small" onClick={reset}>
                Azzera filtri
              </button>
            )}
          </div>
          {box(
            'dipendente',
            'Dipendenti',
            staff,
            staffColumns,
            canWrite
              ? 'Nessun dipendente: aggiungilo con «Nuovo dipendente».'
              : 'Nessun dipendente.',
          )}
          {box(
            'collaboratore',
            'Collaboratori',
            collaborators,
            collaboratorColumns,
            canWrite
              ? 'Nessun collaboratore: aggiungilo con «Nuovo collaboratore».'
              : 'Nessun collaboratore.',
          )}
        </>
      )}
      {creating && (
        <EmployeeDialog
          employee={null}
          relationship={creating}
          initialSubjectId={fromSubject}
          onClose={() => setCreating(null)}
          onSaved={(saved) => void navigate(`/organizzazione/persone/${saved.id}`)}
        />
      )}
    </section>
  );
}
